import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import ProgramFramework from '../components/ProgramFramework';
import ReviewSnippets from '../components/ReviewSnippets';
import { PROGRAM_FAQS } from '../data/programFaqs';
import { Activity, ArrowRight, Stethoscope, TrendingDown, HeartPulse } from 'lucide-react';

const PrediabetesProgramPage = () => (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>Prediabetes Program | Doctor-Led Diet &amp; Lifestyle Support | TCUBE FITNESS</title>
      <meta name="description" content="A doctor-led prediabetes program that uses your reports, lifestyle and medications to build a personalized diet and lifestyle plan supporting healthier blood sugar. Act early, 100% online." />
      <meta name="keywords" content="prediabetes program, prediabetes diet, prediabetes treatment, borderline diabetes, high HbA1c diet, insulin resistance program, prevent type 2 diabetes, prediabetes doctor, prediabetes nutrition India" />
      <link rel="canonical" href="https://tcubefitness.com/prediabetes-program" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="Doctor-Led Prediabetes Program"
      description="A doctor-led, personalized prediabetes diet and lifestyle program to support healthier blood sugar, built around your reports and medical history."
      url="/prediabetes-program"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'Programs', path: '/#programs' },
        { name: 'Prediabetes Program', path: '/prediabetes-program' },
      ]}
      faq={(PROGRAM_FAQS['prediabetes-program'] || []).map((f) => ({ question: f.q, answer: f.a }))}
    />

    <AnnouncementBanner />
    <Header />

    <main id="main-content" tabIndex={-1}>
      <section className="pt-32 sm:pt-36 pb-16 relative overflow-hidden">
        <div className="absolute top-10 right-1/4 w-80 h-80 bg-emerald-600/10 rounded-full blur-3xl" aria-hidden="true"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center max-w-3xl">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <Activity className="w-4 h-4" aria-hidden="true" /> Doctor-Led Prediabetes Program
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Prediabetes Program: <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Act Early, the Right Way</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
            Prediabetes is an early signal, and it is the best time to take action. Led by a General Medicine (Internal Medicine) physician, this program uses your reports, lifestyle and medications to build a practical, personalized plan that supports healthier blood sugar. It complements, and never replaces, your medical care.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book-consultation?from=prediabetes-program" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all shadow-lg shadow-emerald-500/25" data-testid="prediabetes-cta-book">
              Book a Diabetes Nutrition Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/health-analysis" className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all" data-testid="prediabetes-cta-analysis">
              Start Full Body Health Analysis
            </Link>
          </div>
        </div>
      </section>

      {/* Why act now */}
      <section className="py-14 relative" aria-labelledby="prediabetes-why-heading">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <h2 id="prediabetes-why-heading" className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">Why prediabetes is the right time to act</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: TrendingDown, t: 'A reversible window', d: 'For many people, sustained nutrition and lifestyle changes can support healthier blood glucose when started early. Outcomes vary and are monitored medically.' },
              { icon: Stethoscope, t: 'Personalized, not generic', d: 'We account for your HbA1c, family history, weight, insulin resistance and any medications, not a one-size-fits-all diet.' },
              { icon: HeartPulse, t: 'Protects long-term health', d: 'Acting on prediabetes supports metabolic and heart health, alongside regular check-ups with your doctor.' },
            ].map((c, i) => (
              <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                <c.icon className="w-6 h-6 text-emerald-400 mb-3" aria-hidden="true" />
                <h3 className="text-white font-bold mb-2">{c.t}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-sm mt-6 text-center max-w-2xl mx-auto">
            This program supports blood sugar management through nutrition and lifestyle. It does not guarantee reversal and does not replace medical monitoring, testing or prescribed treatment.
          </p>
        </div>
      </section>

      <ReviewSnippets
        ids={["mahesh-prediabetes", "aruna-diabetes"]}
        heading="Real client experiences"
        subheading="Genuine stories from people we have supported. Individual results vary."
      />

      {/* Related reading */}
      <section className="py-12" aria-labelledby="prediabetes-related-heading">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <h2 id="prediabetes-related-heading" className="text-xl sm:text-2xl font-bold text-white mb-5 text-center">Learn more</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { label: 'Prediabetes Diet and Lifestyle: Practical Steps', to: '/blog/prediabetes-diet-and-lifestyle' },
              { label: 'Insulin Resistance: Symptoms, Diet & Exercise', to: '/blog/insulin-resistance-symptoms-diet-exercise' },
              { label: 'Diabetes & Metabolic Health Hub', to: '/hub/diabetes-metabolic-health' },
              { label: 'Doctor-Led Diabetes & Clinical Nutrition', to: '/medical-nutrition-therapy' },
            ].map((l, i) => (
              <Link key={i} to={l.to} className="flex items-center justify-between gap-3 bg-slate-900/60 border border-slate-800 hover:border-emerald-500/40 rounded-2xl px-5 py-4 text-slate-200 hover:text-white transition-colors" data-testid={`prediabetes-related-${i}`}>
                <span className="text-sm font-medium">{l.label}</span>
                <ArrowRight className="w-4 h-4 text-emerald-400 flex-shrink-0" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ProgramFramework slug="prediabetes-program" />
    </main>

    <Footer />
    <FloatingWhatsApp />
    <MobileBottomNav />
  </div>
);

export default PrediabetesProgramPage;
